import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { CheckCircle, PhotoCamera, MusicNote, SmartDisplay, PersonOutline } from '@mui/icons-material';
import { confetti_I } from '../../assets/animations';
import { SafeLottie } from '../Lottie/SafeLottie';
import { ICampaign } from '../../redux/buzzbridge/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface Props {
    campaign: ICampaign;
    onBackToDashboard: () => void;
    onViewApplications: () => void;
    variant?: 'page' | 'modal';
}

const confettiStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
    zIndex: 0,
};

const ConfettiOverlay: React.FC = () => <SafeLottie animationData={confetti_I} loop={false} style={confettiStyle} />;

function platformRow(p: string) {
    const key = p.toLowerCase();
    const label = p.charAt(0).toUpperCase() + p.slice(1);
    if (key.includes('tiktok')) return { label, icon: <MusicNote sx={{ fontSize: 20 }} /> };
    if (key.includes('youtube')) return { label, icon: <SmartDisplay sx={{ fontSize: 20 }} /> };
    return { label, icon: <PhotoCamera sx={{ fontSize: 20 }} /> };
}

export const CampaignApplicationSuccess: React.FC<Props> = ({
    campaign,
    onBackToDashboard,
    onViewApplications,
    variant = 'page',
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const isModal = variant === 'modal';
    const heroImage = campaign.feature_image || campaign.assets?.[0]?.url;
    const platformRows = (campaign.platforms?.length ? campaign.platforms : ['instagram', 'tiktok', 'youtube']).map(
        platformRow,
    );

    return (
        <Box
            sx={{
                minHeight: isModal ? 0 : '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: isModal ? 'flex-start' : 'center',
                px: isModal ? 0 : 3,
                py: isModal ? 0 : 6,
                position: 'relative',
                overflow: isModal ? 'visible' : 'hidden',
            }}>
            {isModal ? (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minHeight: 160,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        zIndex: 0,
                    }}>
                    <ConfettiOverlay />
                </Box>
            ) : (
                <ConfettiOverlay />
            )}

            {/* Decorative glow */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    display: isModal ? 'none' : 'block',
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-10%',
                        right: '-10%',
                        width: 500,
                        height: 500,
                        bgcolor: `${t.highlight}08`,
                        borderRadius: '50%',
                        filter: 'blur(120px)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '-10%',
                        left: '-10%',
                        width: 400,
                        height: 400,
                        bgcolor: `${t.btnCustomColor}08`,
                        borderRadius: '50%',
                        filter: 'blur(100px)',
                    }}
                />
            </Box>

            {/* Success icon */}
            <Box sx={{ position: 'relative', mb: isModal ? 2 : 4, zIndex: 1 }}>
                <Box
                    sx={{
                        width: 96,
                        height: 96,
                        borderRadius: '50%',
                        bgcolor: `${t.highlight}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: -12,
                            borderRadius: '50%',
                            border: `1px solid ${t.highlight}30`,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: -24,
                            borderRadius: '50%',
                            border: `1px solid ${t.highlight}15`,
                        }}
                    />
                    <CheckCircle sx={{ fontSize: 48, color: t.highlight }} />
                </Box>
            </Box>

            {/* Text */}
            <Box sx={{ textAlign: 'center', maxWidth: 500, mb: isModal ? 3 : 6, zIndex: 1 }}>
                <Typography
                    sx={{
                        fontSize: isModal ? { xs: 26, sm: 30 } : { xs: 32, md: 44 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        background: `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange}, ${t.highlight})`,
                        backgroundSize: '200% auto',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                    }}>
                    Application Sent Successfully!
                </Typography>
                <Typography sx={{ color: t.subtle_white, fontSize: 18, lineHeight: 1.6 }}>
                    Your profile and connected socials have been shared with the brand. They will review your
                    application soon.
                </Typography>
            </Box>

            {/* Campaign summary card */}
            <Box sx={{ width: '100%', maxWidth: 420, mb: isModal ? 3 : 6, zIndex: 1 }}>
                <Box sx={{ bgcolor: t.card_grey, borderRadius: 3, p: 0.5, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            bgcolor: t.deep_grey_lighter,
                            borderRadius: 2.5,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                            {heroImage && (
                                <Box
                                    component="img"
                                    src={heroImage}
                                    alt={campaign.title}
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: 3,
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.15em',
                                        color: t.btnCustomColor,
                                        mb: 0.5,
                                    }}>
                                    Active Campaign
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 20,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.true_white,
                                    }}>
                                    {campaign.title}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 1,
                                        color: t.subtle_white,
                                    }}>
                                    <PersonOutline sx={{ fontSize: 16 }} />
                                    <Typography sx={{ fontSize: 14 }}>UGC Creator</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Social platforms */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${Math.min(platformRows.length, 3)}, 1fr)`,
                                gap: 1.5,
                                pt: 2,
                                borderTop: `1px solid ${t.grey}18`,
                            }}>
                            {platformRows.map(({ label, icon }, idx) => (
                                <Box
                                    key={`${label}-${idx}`}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 1.5,
                                        bgcolor: t.wrapperColor,
                                        borderRadius: 3,
                                        color: t.subtle_white,
                                    }}>
                                    {icon}
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            textTransform: 'uppercase',
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                            mt: 0.5,
                                        }}>
                                        {label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* CTAs */}
            <Box sx={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 2, zIndex: 1 }}>
                <Button
                    onClick={onBackToDashboard}
                    fullWidth
                    sx={{
                        py: 2,
                        borderRadius: 10,
                        background: `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange})`,
                        color: t.button_text,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        fontSize: 18,
                        textTransform: 'none',
                        boxShadow: `0 10px 30px ${t.highlight}30`,
                        '&:active': { transform: 'scale(0.98)' },
                    }}>
                    Back to Dashboard
                </Button>
                <Button
                    onClick={onViewApplications}
                    fullWidth
                    sx={{
                        py: 2,
                        borderRadius: 10,
                        bgcolor: t.card_grey,
                        border: `1px solid ${t.grey}30`,
                        color: t.true_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 700,
                        fontSize: 18,
                        textTransform: 'none',
                        '&:hover': { bgcolor: t.wrapperColor },
                        '&:active': { transform: 'scale(0.98)' },
                    }}>
                    View My Applications
                </Button>
            </Box>
        </Box>
    );
};
